import { WizardStepTitle } from "app/modules/shared/wizard/WizardStepTitle";
import { useDispatch, useSelector } from "react-redux";
import {
  selectServiceChargeInvoice,
  setCurrentStep,
  setServiceChargeInvoiceModel,
} from "../../ServiceChargeInvoicingSlice";
import { ServiceChargeInvoicingSteps } from "../../ServiceChargeInvoiceWizardSteps";
import { WizardStepperFooter } from "app/modules/shared/wizard/WizardStepFooter";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import DevelopmentSelectMulti from "app/modules/shared/selectComponents/developmentSelect/DevelopmentSelectMulti";
import PropertyManagerSelectMulti from "app/modules/shared/selectComponents/propertyManagerSelect/PropertyManagerSelectMulti";
import FundScheduleChargeTypeSelectMulti from "app/modules/shared/selectComponents/fundScheduleChargeTypeSelect/FundScheduleChargeTypeSelectMulti";

const DevelopmentsOrPropertyManagers: React.FC = () => {
  const dispatch = useDispatch();
  const serviceChargeInvoice = useSelector(selectServiceChargeInvoice);

  const [isDevelopment, setIsDevelopment] = useState<boolean>(
    (serviceChargeInvoice.propertyManagers?.length ?? 0) == 0
  );
  const [defaultDevelopments, setDefaultDevelopments] = useState<string[] | undefined>(
    serviceChargeInvoice.developments
  );
  const [defaultPropertyManagers, setDefaultPropertManagers] = useState<string[] | undefined>(
    serviceChargeInvoice.propertyManagers
  );
  const [defaultChargeTypes, setDefaultChargeTypes] = useState<number[] | undefined>(
    serviceChargeInvoice.fundScheduleChargeTypes
  );

  const disableContinueButton = useMemo(() => {
    const hasDevelopmentOrPM = isDevelopment
      ? (defaultDevelopments?.length ?? 0) > 0
      : (defaultPropertyManagers?.length ?? 0) > 0;

    const hasChargeTypes = defaultChargeTypes === undefined || defaultChargeTypes.length > 0;

    return hasDevelopmentOrPM && hasChargeTypes;
  }, [isDevelopment, defaultDevelopments, defaultPropertyManagers, defaultChargeTypes]);

  function goToPreviousStep() {
    updateServiceChargeInvoiceModel();
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.ServiceChargeDateCodes));
  }

  function goToNextStep() {
    updateServiceChargeInvoiceModel();
    dispatch(setCurrentStep(ServiceChargeInvoicingSteps.GraceDays));
  }

  function updateServiceChargeInvoiceModel() {
    if (isDevelopment) {
      dispatch(
        setServiceChargeInvoiceModel({
          ...serviceChargeInvoice,
          developments: defaultDevelopments,
          propertyManagers: undefined,
          fundScheduleChargeTypes: defaultChargeTypes,
        })
      );
    } else {
      dispatch(
        setServiceChargeInvoiceModel({
          ...serviceChargeInvoice,
          developments: undefined,
          propertyManagers: defaultPropertyManagers,
          fundScheduleChargeTypes: defaultChargeTypes,
        })
      );
    }
  }

  return (
    <>
      <WizardStepTitle
        stepNumber={ServiceChargeInvoicingSteps.DevelopmentsOrPropertyManagers}
        title="Select Developments or Property Managers"
        subtitle="Please select the Development(s) or Property Manager(s) for which you would like to process invoices"
      ></WizardStepTitle>
      <div className="d-flex flex-column justify-content-center">
        <div className="d-flex flex-column align-items-center me-2">
          <div className="d-flex gap-3 mb-1">
            <div>
              <input
                data-test-id="developments-or-property-managers-step__development-radio"
                id="developmentRadioSelector"
                type="radio"
                className="form-check-input"
                name="developmentOrPropertyManagerSelector"
                checked={isDevelopment}
                onChange={(e) => setIsDevelopment(e.target.checked)}
              ></input>
              <label htmlFor="developmentRadioSelector" className="ps-1 form-label">
                Development
              </label>
            </div>
            <div>
              <input
                data-test-id="developments-or-property-managers-step__property-manager-radio"
                id="propertyManagerRadioSelector"
                type="radio"
                className="form-check-input"
                name="developmentOrPropertyManagerSelector"
                checked={!isDevelopment}
                onChange={(e) => setIsDevelopment(!e.target.checked)}
              ></input>
              <label htmlFor="propertyManagerRadioSelector" className="ps-1 form-label">
                Property Manager
              </label>
            </div>
          </div>
          {isDevelopment ? (
            <DevelopmentSelectMulti
              data-test-id="developments-or-property-managers-step__developments-select"
              defaultAll={isDevelopment && defaultDevelopments == undefined}
              handleChange={(selections) => {
                setDefaultDevelopments(selections);
              }}
              defaultSome={defaultDevelopments}
            ></DevelopmentSelectMulti>
          ) : (
            <PropertyManagerSelectMulti
              data-test-id="developments-or-property-managers-step__property-managers-select"
              defaultAll={!isDevelopment && defaultPropertyManagers == undefined}
              handleChange={(selections) => {
                setDefaultPropertManagers(selections);
              }}
              defaultSome={defaultPropertyManagers}
            ></PropertyManagerSelectMulti>
          )}
        </div>

        <div className="d-flex flex-column mt-15">
          <div className="fw-bold">Select Fund Schedule Charge Types</div>
          <div className="text-muted">
            Please select the Fund Schedule Charge Types for which you would like to process invoices
          </div>
          <div className="d-flex flex-column align-items-center mt-3">
            <FundScheduleChargeTypeSelectMulti
              data-test-id="developments-or-property-managers-step__charge-types-select"
              defaultAll={defaultChargeTypes === undefined}
              defaultSome={defaultChargeTypes}
              handleChange={(selections) => setDefaultChargeTypes(selections)}
            />
          </div>
        </div>
      </div>
      <WizardStepperFooter>
        <div className="d-flex justify-content-between">
          <div>
            <button
              data-test-id="developments-or-property-managers-step__go-back-button"
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => goToPreviousStep()}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span className="ms-1">Go back</span>
            </button>
          </div>
          <div>
            <button
              data-test-id="developments-or-property-managers-step__continue-button"
              className="btn btn-success btn-sm"
              type="button"
              onClick={() => goToNextStep()}
              disabled={!disableContinueButton}
            >
              <span className="me-1">Continue</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>
      </WizardStepperFooter>
    </>
  );
};

export default DevelopmentsOrPropertyManagers;
